import { resolveDriver, type Change } from './declarations.ts'
import type { PitStopDoc } from './timing'

/**
 * How long a driver has been driving, as the regulations measure it.
 *
 * There are two different things called a stint here, and conflating them is a
 * real bug rather than a naming quibble.
 *
 * A **pit stint** is bounded by every pit visit. That is what the interpreter
 * detects, what the stint chart draws, and what fuel and pace analysis want.
 *
 * A **driver stint** is bounded by driver changes, and it is what a driving-time
 * limit measures. The wording is "including pit stops, measured from pit-out to
 * pit-in" — so a car that comes in mid-stint for a repair or a drive-through
 * penalty, with the same driver staying in, has NOT reset anything. That pit
 * lane time counts against the driver's stint.
 *
 * Building the limit on pit stints would hand a driver a fresh ninety minutes
 * every time they took a penalty, which is precisely what the clause exists to
 * prevent.
 *
 * Computed here rather than in the interpreter because the boundaries depend on
 * who was in the car, and that comes from the organisation's own declarations —
 * private data the pipeline cannot see. The interpreter keeps producing pit
 * stints; this turns them into driver stints.
 */

/** What the pit stop record gives us about one visit, once times are resolved. */
export interface PitVisit {
  stopNumber: number
  lap: number
  /** When the car entered the pits. */
  pitIn: Date
  /** When it left. */
  pitOut: Date
  /**
   * The stop was inferred rather than observed, so its times are a poorer
   * answer than the others. Carried through so a derived span can say it is
   * standing on softer ground rather than quietly claiming otherwise.
   */
  inferred: boolean
  isFuelStop: boolean
}

/**
 * Resolve a stored pit stop into the two instants that matter.
 *
 * The vendor's own pit_in_time and pit_out_time are preferred but usually
 * absent — RIS sent neither for a single stop of the Spa archive. What is
 * always present is `completedAt`, the poll at which the stop was seen to
 * finish, and `stopLengthMs`, the vendor's own duration. Together those give
 * both instants to within a poll interval: seconds, against limits measured in
 * hours.
 *
 * Null when neither route works, rather than a guess. A stop with no duration
 * cannot say when it began, and inventing one would put a driver's limit in the
 * wrong place.
 */
export function pitVisitOf(stop: PitStopDoc): PitVisit | null {
  const completed = stop.completedAt?.toDate?.()
  if (!completed) return null

  const length = stop.stopLengthMs
  if (length == null || length < 0) return null

  return {
    stopNumber: stop.stopNumber,
    lap: stop.lap,
    pitIn: new Date(completed.getTime() - length),
    pitOut: completed,
    inferred: !!stop.isRecovery,
    isFuelStop: !!stop.isFuelStop,
  }
}

export interface DriverStint {
  /**
   * The organisation's driver id: declared, or the timing system's name
   * matched to the roster. Null where neither says.
   */
  driverId: string | null
  /** The timing system's own name, where that is all there is. */
  name?: string | null
  /** True when the team declared this driver rather than the feed naming them. */
  declared?: boolean
  /** Pit-out that started it, or the session start for the first. */
  from: Date
  /** Pit-in that ended it, or null while it is still running. */
  to: Date | null
  /**
   * The car has been in the pits since this instant and has not come out.
   *
   * Only ever set on the stint in progress. Whether the visit is a driver
   * change, a repair with the same driver, or the end of the car's race is not
   * knowable until it leaves — or fails to. So the clock is held here rather
   * than run on: if the same driver comes back out the visit becomes interior
   * and the time is added back; if the car never leaves, the stint ended when
   * it came in, which is what a car parked in the garage at the flag needs to
   * say instead of "fifty-four hours".
   */
  pausedAt: Date | null
  /** Pit visits inside it that did NOT change driver — penalties, repairs. */
  visitsInside: PitVisit[]
  /** True when any boundary or interior visit was inferred rather than observed. */
  uncertain: boolean
  /**
   * Laps completed in it, where the stint chart's pit stints say.
   *
   * Summed from the interpreter's own per-stint counts rather than counted from
   * the laps a dashboard happens to hold, which are a recent window: a ninety
   * minute stint is longer than the window. Absent when the stint was derived
   * from the stops alone.
   */
  laps?: number
}

/** How a driver change is decided. */
export interface DrivingOptions {
  /**
   * Every stop changes driver, so every pit visit is a boundary.
   *
   * True for a regime with driverChangeRequired: 'everyStop' — the Spa 25H — and
   * it makes the derivation exact without anybody declaring anything.
   */
  everyStopChangesDriver?: boolean
  /**
   * When the car entered the pits, if it is in there now and has not come out.
   *
   * The caller knows this from the live standings and the last line crossing;
   * this module does not read either. Ignored when it predates the stint in
   * progress, because then the car has demonstrably left since.
   */
  inPitsSince?: Date | null
  /**
   * Who the timing system says was driving at an instant, where the team has
   * not declared anyone.
   *
   * The same answer the stint chart shows: the name the feed settled on for
   * the pit stint containing that instant, matched to the roster where it can
   * be. Without it an undeclared race had no driver changes at all, and the
   * planner drew the whole of it as one stint with every stop a tick inside.
   */
  vendorAt?: (at: Date) => { driverId: string | null; name: string | null } | null
}

interface Who {
  /** What a change is judged on: the roster id, else the feed's name. */
  key: string | null
  driverId: string | null
  name: string | null
  declared: boolean
}

/**
 * Turn a car's pit visits into driver stints.
 *
 * A visit is a boundary when the driver differs across it, or when the regime
 * says every stop changes driver. The driver is the team's declaration where
 * there is one and the timing system's otherwise. Everything else is
 * interior: the clock keeps running through it.
 *
 * `sessionStart` begins the first stint. A grid start has no pit-out to measure
 * from, and a pit-lane start's first pit-out is simply later, so taking the
 * later of the two covers both without a special case.
 */
export function driverStints(
  car: string,
  visits: readonly PitVisit[],
  declarations: readonly Change[],
  sessionStart: Date,
  now: Date,
  options: DrivingOptions = {},
): DriverStint[] {
  const ordered = [...visits].sort((a, b) => a.pitIn.getTime() - b.pitIn.getTime())

  const who = (at: Date): Who => {
    const resolved = resolveDriver(declarations, car, at)
    if (resolved.source === 'team') {
      return { key: resolved.driverId, driverId: resolved.driverId, name: null, declared: true }
    }
    const v = options.vendorAt?.(at) ?? null
    if (v && (v.driverId || v.name)) {
      return { key: v.driverId ?? `vendor:${v.name}`, driverId: v.driverId, name: v.name, declared: false }
    }
    return { key: null, driverId: null, name: null, declared: false }
  }

  const out: DriverStint[] = []
  let from = sessionStart
  let inside: PitVisit[] = []
  let uncertain = false

  for (const visit of ordered) {
    // A visit before the session started belongs to nothing — a car released
    // from the pit lane before the flag, most often.
    if (visit.pitOut <= sessionStart) continue

    const changed =
      options.everyStopChangesDriver || who(visit.pitIn).key !== who(visit.pitOut).key

    if (!changed) {
      // The clock keeps running. This is the whole point.
      inside.push(visit)
      uncertain = uncertain || visit.inferred
      continue
    }

    const before = who(from)
    out.push({
      driverId: before.driverId,
      name: before.name,
      declared: before.declared,
      from,
      to: visit.pitIn,
      pausedAt: null,
      visitsInside: inside,
      uncertain: uncertain || visit.inferred,
    })
    from = visit.pitOut
    inside = []
    uncertain = visit.inferred
  }

  // The stint in progress. `to` is null rather than `now`, so a caller measuring
  // it has to decide what "now" means — which matters when the race is stopped.
  const parked = options.inPitsSince ?? null
  const last = who(from)
  out.push({
    driverId: last.driverId,
    name: last.name,
    declared: last.declared,
    from,
    to: null,
    pausedAt: parked && parked.getTime() > from.getTime() ? parked : null,
    visitsInside: inside,
    uncertain,
  })
  void now
  return out
}

/**
 * One pit stint with its driver already resolved, the way the stint chart
 * resolves it: the team's declaration at the stint's start, else the name the
 * timing system settled on for the stint.
 */
export interface PitStintDriver {
  from: Date
  /** The completion of its last lap, or null if nothing dates the end. */
  to: Date | null
  /** The in-lap, which is the lap a pit stop record is filed against. */
  lastLap: number
  /** Laps completed in this pit stint, as the interpreter counted them. */
  laps?: number
  /** What sameness is judged on: the roster id, else the feed's name. */
  key: string | null
  driverId: string | null
  name: string | null
  declared: boolean
}

/**
 * Driver stints as the stint chart shows them, with adjacent pit stints of
 * the same driver joined.
 *
 * The stint chart is the reference: it is what the pit wall reads, and a
 * planner that disagreed with it was a planner nobody could trust. So this
 * does not re-derive who was driving from individual stops; it takes the pit
 * stints and their drivers as given and merges a run of them into one driver
 * stint. The stop records only supply exact times at each boundary: a driver
 * stint runs from the pit-out of the stop before it to the pit-in of the stop
 * that ends it, which is how the regulations measure one. Where a stop has no
 * record the pit stint's own times stand in. The stops inside a merged run are
 * its interior visits.
 *
 * `closeLast` ends the final stint where its last lap ended; without it the
 * final stint is the one in progress and `to` is null.
 */
export function coalescePitStints(
  pits: readonly PitStintDriver[],
  visits: readonly PitVisit[],
  options: { everyStopChangesDriver?: boolean; inPitsSince?: Date | null; closeLast?: boolean } = {},
): DriverStint[] {
  const ordered = [...pits].sort((a, b) => a.from.getTime() - b.from.getTime())
  if (ordered.length === 0) return []
  const stopAfter = (i: number) => visits.find((v) => v.lap === ordered[i]!.lastLap) ?? null

  const out: DriverStint[] = []
  let a = 0
  while (a < ordered.length) {
    let b = a
    while (
      b + 1 < ordered.length &&
      !options.everyStopChangesDriver &&
      ordered[b + 1]!.key === ordered[a]!.key
    ) {
      b++
    }
    const first = ordered[a]!
    const lastInRun = ordered[b]!
    const before = a > 0 ? stopAfter(a - 1) : null
    const after = stopAfter(b)
    const isLast = b === ordered.length - 1

    const inside: PitVisit[] = []
    for (let i = a; i < b; i++) {
      const v = stopAfter(i)
      if (v) inside.push(v)
    }

    let to: Date | null
    if (!isLast) to = after?.pitIn ?? lastInRun.to ?? ordered[b + 1]!.from
    else if (options.closeLast) to = lastInRun.to
    else to = null

    const parked = isLast && !options.closeLast ? (options.inPitsSince ?? null) : null
    const from = before?.pitOut ?? first.from
    // Only when every pit stint in the run says. A total missing one of its
    // parts is a smaller number that looks just as sure.
    const counts = ordered.slice(a, b + 1).map((p) => p.laps)
    const laps = counts.every((n) => n != null) ? counts.reduce((sum, n) => sum! + n!, 0) : undefined
    out.push({
      driverId: first.driverId,
      name: first.name,
      declared: first.declared,
      from,
      to,
      pausedAt: parked && parked.getTime() > from.getTime() ? parked : null,
      visitsInside: inside,
      uncertain: !!before?.inferred || !!after?.inferred || inside.some((v) => v.inferred),
      ...(laps != null ? { laps } : {}),
    })
    a = b + 1
  }
  return out
}

/**
 * How long a stint has run, in milliseconds.
 *
 * `now` should be the session's own clock, not the viewer's: a stint measured
 * against a browser clock keeps growing for days after the flag. The dashboard
 * has `asAt()` for exactly this.
 */
export function stintDuration(stint: DriverStint, now: Date): number {
  const end = stint.to ?? stint.pausedAt ?? now
  return Math.max(0, end.getTime() - stint.from.getTime())
}

export interface DriverTotals {
  driverId: string
  /** Total driving time, including pit time inside a stint. */
  totalMs: number
  /** How many driver stints they have taken. */
  stints: number
  /** True while they are the one in the car. */
  current: boolean
}

/**
 * What each driver has done so far.
 *
 * Only stints with a known driver are counted, and the caller is told how many
 * were not, because a cumulative limit computed from partial attribution is
 * worse than no number: it reads as "you are fine" right up until it is wrong.
 */
export function driverTotals(
  stints: readonly DriverStint[],
  now: Date,
): { totals: DriverTotals[]; unattributed: number } {
  const byDriver = new Map<string, DriverTotals>()
  let unattributed = 0

  stints.forEach((stint, i) => {
    if (!stint.driverId) {
      unattributed++
      return
    }
    const found = byDriver.get(stint.driverId) ?? {
      driverId: stint.driverId,
      totalMs: 0,
      stints: 0,
      current: false,
    }
    found.totalMs += stintDuration(stint, now)
    found.stints++
    found.current = found.current || i === stints.length - 1
    byDriver.set(stint.driverId, found)
  })

  return {
    totals: [...byDriver.values()].sort((a, b) => b.totalMs - a.totalMs),
    unattributed,
  }
}
